<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '../../composables/useAppStore'
import { formatCurrency } from '../../lib/date'

const props = defineProps({
  collapsed: { type: Boolean, default: false }
})

const emit = defineEmits(['toggle'])

const route = useRoute()
const { state, currentWeek, openShiftForm } = useAppStore()

const primaryItems = [
  { to: '/', label: 'Главная', description: 'Обзор дохода', icon: 'home' },
  { to: '/shifts', label: 'Смены', description: 'История работы', icon: 'route' },
  { to: '/payouts', label: 'Выплаты', description: 'Периоды и факты', icon: 'wallet' },
  { to: '/analytics', label: 'Аналитика', description: 'Динамика и показатели', icon: 'chart' }
]

const roleLabel = computed(() => state.settings.role === 'picker' ? 'Сборщик' : 'Курьер')
const weekNet = computed(() => currentWeek.value?.summary?.net || 0)
const isSettingsActive = computed(() => route.name === 'settings')
</script>

<template>
  <aside class="sticky top-0 flex h-dvh w-full flex-col border-r border-app-border bg-app-surface px-3 py-4 shadow-[6px_0_24px_rgba(15,23,42,0.03)]">
    <div class="flex min-h-14 items-center gap-3 px-2">
      <div class="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-app-control bg-slate-950 text-lg font-black text-white shadow-sm">
        <div class="absolute inset-0 bg-gradient-to-br from-brand-300 via-brand-500 to-slate-950"></div>
        <span class="relative">Д</span>
      </div>
      <div class="hidden min-w-0 lg:block" :class="{ 'lg:hidden': collapsed }">
        <p class="truncate text-base font-black text-app-text">ДоходПро</p>
        <p class="truncate text-xs font-semibold text-app-muted">{{ roleLabel }} · локальный режим</p>
      </div>
    </div>

    <button
      type="button"
      class="mt-5 flex min-h-12 w-full items-center justify-center gap-2 rounded-app-control bg-brand-500 px-3 text-sm font-black text-white shadow-lg shadow-brand-500/20 transition hover:bg-brand-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-100 active:scale-[0.98]"
      :title="collapsed ? 'Добавить смену' : undefined"
      @click="openShiftForm()"
    >
      <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
      <span class="hidden lg:inline" :class="{ 'lg:hidden': collapsed }">Новая смена</span>
    </button>

    <nav class="mt-6 flex-1 space-y-1" aria-label="Основная навигация">
      <RouterLink
        v-for="item in primaryItems"
        :key="item.to"
        :to="item.to"
        class="group flex min-h-12 items-center gap-3 rounded-app-control px-3 text-app-muted transition hover:bg-slate-100 hover:text-app-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-200"
        active-class="!bg-slate-950 !text-white shadow-sm"
        :title="collapsed ? item.label : undefined"
      >
        <span class="flex h-6 w-6 shrink-0 items-center justify-center">
          <svg v-if="item.icon === 'home'" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/></svg>
          <svg v-else-if="item.icon === 'route'" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M6 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M18 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M6 13V8a3 3 0 0 1 3-3h6"/><path d="M18 11v5a3 3 0 0 1-3 3H9"/></svg>
          <svg v-else-if="item.icon === 'wallet'" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16v12H4z"/><path d="M4 7l3-3h13"/><path d="M16 13h4"/></svg>
          <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19V5"/><path d="M4 19h16"/><path d="M8 16v-5"/><path d="M12 16V8"/><path d="M16 16v-9"/></svg>
        </span>
        <span class="hidden min-w-0 lg:block" :class="{ 'lg:hidden': collapsed }">
          <span class="block truncate text-sm font-black">{{ item.label }}</span>
          <span class="block truncate text-[11px] font-semibold opacity-70">{{ item.description }}</span>
        </span>
      </RouterLink>
    </nav>

    <div class="space-y-2 border-t border-app-border pt-3">
      <div class="hidden rounded-app-card bg-slate-50 p-3 lg:block" :class="{ 'lg:hidden': collapsed }">
        <p class="text-[11px] font-bold uppercase tracking-wider text-app-muted">Текущая неделя</p>
        <p class="mt-1 text-base font-black text-app-text">{{ formatCurrency(weekNet, state.settings.currency) }}</p>
        <p class="mt-1 text-xs font-semibold text-app-muted">Данные хранятся только на устройстве</p>
      </div>

      <RouterLink
        to="/settings"
        class="flex min-h-12 items-center gap-3 rounded-app-control px-3 text-app-muted transition hover:bg-slate-100 hover:text-app-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-200"
        :class="isSettingsActive ? 'bg-slate-950 text-white shadow-sm' : ''"
        :title="collapsed ? 'Настройки' : undefined"
      >
        <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.12 2.12-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.55V20h-3v-.09A1.7 1.7 0 0 0 10.68 18.36a1.7 1.7 0 0 0-1.88.34l-.06.06-2.12-2.12.06-.06A1.7 1.7 0 0 0 7.02 15a1.7 1.7 0 0 0-1.55-1.03H5v-3h.47A1.7 1.7 0 0 0 7.02 9.94a1.7 1.7 0 0 0-.34-1.88L6.62 8l2.12-2.12.06.06a1.7 1.7 0 0 0 1.88.34A1.7 1.7 0 0 0 11.71 4.73V4h3v.73a1.7 1.7 0 0 0 1.03 1.55 1.7 1.7 0 0 0 1.88-.34l.06-.06L19.8 8l-.06.06a1.7 1.7 0 0 0-.34 1.88A1.7 1.7 0 0 0 20.95 11H21v3h-.05A1.7 1.7 0 0 0 19.4 15Z"/></svg>
        <span class="hidden text-sm font-black lg:inline" :class="{ 'lg:hidden': collapsed }">Настройки</span>
      </RouterLink>

      <button
        type="button"
        class="hidden min-h-11 w-full items-center justify-center gap-2 rounded-app-control text-xs font-black text-app-muted transition hover:bg-slate-100 hover:text-app-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-200 lg:flex"
        :aria-label="collapsed ? 'Развернуть боковую панель' : 'Свернуть боковую панель'"
        @click="emit('toggle')"
      >
        <svg class="h-4 w-4 transition-transform" :class="collapsed ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        <span v-if="!collapsed">Компактный режим</span>
      </button>
    </div>
  </aside>
</template>
